// GH-841 — Hebrew labels for the support-circle logs: the UserActivityLog rows (actionCategory SUPPORT_CIRCLE), the
// support_circle_events rows and the "יומן" (SupportCircleTimeline). The reason codes mirror the backend registry
// (grouphug-users-backend api/supportCircles/supportCircleReasons.ts, plan 1.7) — EVERY code there must have a label
// here (the QA "registry coverage" check reads REASON_LABELS as text, so keep it a flat 'code': 'label' literal).
// An unknown code is never hidden: it falls back to the raw code.

export type LogResult = 'SUCCESS' | 'FAILURE' | 'ERROR';
export type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary';

export const RESULT_LABELS: Record<string, string> = {
	SUCCESS: 'הצליח',
	FAILURE: 'נכשל / סורב',
	ERROR: 'תקלה',
};
export const RESULT_COLORS: Record<string, ChipColor> = { SUCCESS: 'success', FAILURE: 'warning', ERROR: 'error' };
export const resultLabel = (r?: string) => (r ? RESULT_LABELS[r] || r : '');
export const resultColor = (r?: string): ChipColor => (r ? RESULT_COLORS[r] || 'default' : 'default');

// ------------------------------------------------------------------ UserActivityLog ActionType (44, plan 1.5)

export const ACTION_LABELS: Record<string, string> = {
	// browsing
	SUPPORT_CIRCLE_LIST_VIEWED: 'צפייה ברשימת המעגלים',
	SUPPORT_CIRCLE_VIEWED: 'צפייה בעמוד המעגל',
	SUPPORT_CIRCLE_TOPIC_SUGGESTED: 'הצעת נושא',
	// registration + payment
	SUPPORT_CIRCLE_REGISTERED: 'הרשמה',
	SUPPORT_CIRCLE_REGISTRATION_EDITED: 'עריכת הרשמה',
	SUPPORT_CIRCLE_SEAT_HELD: 'מקום נשמר לתשלום',
	SUPPORT_CIRCLE_PAYMENT_LINK_OPENED: 'פתיחת קישור התשלום',
	SUPPORT_CIRCLE_PAYMENT_STATUS: 'סטטוס תשלום מ-PayMe',
	SUPPORT_CIRCLE_PAYMENT_CONFIRMED: 'אישור תשלום',
	SUPPORT_CIRCLE_HOLD_EXPIRED: 'שמירת המקום פגה',
	SUPPORT_CIRCLE_REFUND: 'החזר כספי',
	SUPPORT_CIRCLE_UNREGISTERED: 'ביטול הרשמה',
	SUPPORT_CIRCLE_CHANGED_FOR_MEMBER: 'שינוי שהשפיע על המשתתף/ת',
	// attendance confirmation
	SUPPORT_CIRCLE_CONFIRM_PAGE_OPENED: 'פתיחת עמוד "אני מגיע/ה"',
	SUPPORT_CIRCLE_ATTENDANCE_CONFIRMED: 'אישור הגעה',
	// join + room (server)
	SUPPORT_CIRCLE_AUTH_REFUSED: 'סירוב התחברות (auth)',
	SUPPORT_CIRCLE_JOIN_TOKEN: 'בקשת טוקן כניסה',
	SUPPORT_CIRCLE_TOKEN_NOT_USED: 'טוקן שלא נוצל',
	SUPPORT_CIRCLE_ROOM_JOINED: 'נכנס/ה לחדר',
	SUPPORT_CIRCLE_ROOM_LEFT: 'יצא/ה מהחדר',
	SUPPORT_CIRCLE_ROOM_CONNECTION_ABORTED: 'החיבור לחדר נקטע',
	SUPPORT_CIRCLE_ROOM_TRACK: 'מיקרופון / מצלמה בחדר',
	SUPPORT_CIRCLE_NO_SHOW: 'לא הגיע/ה',
	SUPPORT_CIRCLE_ATTENDANCE_SUMMARY: 'סיכום נוכחות',
	// facilitator
	SUPPORT_CIRCLE_STARTED: 'המעגל התחיל',
	SUPPORT_CIRCLE_ENDED: 'המעגל הסתיים',
	SUPPORT_CIRCLE_PARTICIPANT_REMOVED: 'הסרת משתתף/ת',
	SUPPORT_CIRCLE_PARTICIPANT_MUTED: 'השתקת משתתף/ת',
	SUPPORT_CIRCLE_PARTICIPANT_UNMUTED: 'ביטול השתקה של המנחה', // GH-878
	SUPPORT_CIRCLE_PARTICIPANT_RESTORED: 'החזרת משתתף/ת שהוצא/ה', // GH-871
	SUPPORT_CIRCLE_PARTICIPANT_RENAMED: 'שינוי שם בחדר', // GH-871
	SUPPORT_CIRCLE_CHAT_MESSAGE: 'הודעת צ׳אט נדחתה', // GH-871 — a sent message is never stored; only refusals have a row
	SUPPORT_CIRCLE_CHAT_TOGGLED: 'הפעלה / כיבוי של הצ׳אט', // GH-871
	SUPPORT_CIRCLE_FACILITATOR_NO_SHOW: 'המנחה לא הגיע/ה',
	// feedback
	SUPPORT_CIRCLE_FEEDBACK_SUBMITTED: 'משוב',
	// notifications
	SUPPORT_CIRCLE_NOTIFICATION: 'הודעה',
	SUPPORT_CIRCLE_NOTIFICATION_DELIVERY: 'סטטוס מסירה',
	// client telemetry
	SUPPORT_CIRCLE_CLIENT_FLOW: 'אתר / אפליקציה — ניווט',
	SUPPORT_CIRCLE_CLIENT_CONNECT: 'אתר / אפליקציה — חיבור',
	SUPPORT_CIRCLE_CLIENT_MEDIA: 'אתר / אפליקציה — שמע ווידאו',
	SUPPORT_CIRCLE_CLIENT_DISCONNECTED: 'אתר / אפליקציה — ניתוק',
	SUPPORT_CIRCLE_CLIENT_SESSION: 'אתר / אפליקציה — סיכום ישיבה בחדר',
	SUPPORT_CIRCLE_CLIENT_ERROR: 'אתר / אפליקציה — שגיאה',
	// admin (audit)
	SUPPORT_CIRCLE_ADMIN_CREATED: 'אדמין: יצירת מעגל',
	SUPPORT_CIRCLE_ADMIN_UPDATED: 'אדמין: עריכת מעגל',
	SUPPORT_CIRCLE_ADMIN_STATUS_CHANGED: 'אדמין: שינוי סטטוס',
	SUPPORT_CIRCLE_ADMIN_DUPLICATED: 'אדמין: שכפול מעגל',
	SUPPORT_CIRCLE_ADMIN_DELETED: 'אדמין: מחיקת מעגל',
	SUPPORT_CIRCLE_ADMIN_VIEWED: 'אדמין: צפייה במידע',
};
export const isCircleAction = (action?: string) => !!action && action.startsWith('SUPPORT_CIRCLE_');
export const actionLabel = (action?: string) => (action ? ACTION_LABELS[action] || action : '');

// ------------------------------------------------------------------ support_circle_events `type`

/** LiveKit webhook events, stored as type 'lk:<event>' */
export const LK_EVENT_LABELS: Record<string, string> = {
	participant_joined: 'LiveKit: משתתף/ת נכנס/ה',
	participant_left: 'LiveKit: משתתף/ת יצא/ה',
	participant_connection_aborted: 'LiveKit: חיבור נקטע לפני הכניסה',
	track_published: 'LiveKit: מיקרופון/מצלמה פורסמו',
	track_unpublished: 'LiveKit: מיקרופון/מצלמה הוסרו',
	room_started: 'LiveKit: החדר נפתח',
	room_finished: 'LiveKit: החדר נסגר',
	egress_started: 'LiveKit: התחילה הקלטה (לא צפוי!)',
	egress_updated: 'LiveKit: עדכון הקלטה (לא צפוי!)',
	egress_ended: 'LiveKit: הקלטה הסתיימה (לא צפוי!)',
	ingress_started: 'LiveKit: התחיל שידור נכנס (לא צפוי!)',
	ingress_ended: 'LiveKit: שידור נכנס הסתיים (לא צפוי!)',
};

/** client telemetry event names (UAL metadata.event, or events type 'client:<e>'), plan 3.4 */
export const CLIENT_EVENT_LABELS: Record<string, string> = {
	// flow
	circle_opened: 'פתח/ה את עמוד המעגל',
	deep_link_resumed: 'הקישור נפתח אחרי ההתחברות',
	deep_link_lost: 'הקישור אבד בהתחברות (נחת/ה במקום אחר)',
	enter_button_state: 'כפתור הכניסה הופיע / נעלם',
	enter_clicked: 'לחץ/ה "כניסה"',
	room_opened: 'מסך החדר נפתח',
	lobby_entered: 'ממתין/ה בלובי',
	lobby_left: 'יצא/ה מהלובי',
	ended_before_enter: 'המעגל הסתיים לפני שנכנס/ה',
	payment_modal_opened: 'חלון התשלום נפתח',
	payment_modal_closed: 'חלון התשלום נסגר',
	payment_success_msg: 'דף התשלום דיווח הצלחה',
	payment_confirm_timeout: 'אישור התשלום לא הגיע בזמן',
	hold_expired_seen: 'ראה/תה שהמקום השמור פג',
	cancel_clicked: 'לחץ/ה "ביטול הרשמה"',
	feedback_opened: 'פתח/ה את המשוב',
	rules_agreed: 'אישר/ה את כללי המעגל',
	confirm_banner_shown: 'הוצג באנר "אישרת הגעה"',
	// connect
	token_received: 'קיבל/ה טוקן',
	token_failed: 'הטוקן נכשל',
	client_module_missing: 'גרסת אפליקציה בלי מודול הווידאו',
	client_unsupported_browser: 'דפדפן לא נתמך',
	connected: 'התחבר/ה לחדר',
	connect_failed: 'החיבור לחדר נכשל',
	// media
	mic_failed: 'המיקרופון נכשל',
	camera_failed: 'המצלמה נכשלה',
	audio_playback_blocked: 'השמע נחסם בדפדפן (autoplay)',
	audio_playback_unblocked: 'השמע שוחרר',
	video_playback_blocked: 'הווידאו נחסם בדפדפן',
	audio_session_failed: 'הפעלת השמע באפליקציה נכשלה',
	audio_session_started: 'השמע באפליקציה הופעל',
	local_silence_detected: 'המיקרופון שולח שקט',
	mic_published: 'המיקרופון עלה',
	muted_by_facilitator: 'הושתק/ה על ידי המנחה',
	self_unmuted_after_facilitator_mute: 'ביטל/ה השתקה של המנחה',
	mic_toggle_failed: 'הפעלה/כיבוי מיקרופון נכשלו',
	inbound_audio_stalled: 'לא מגיע שמע מהאחרים',
	// disconnect / session / errors
	disconnected: 'התנתק/ה',
	session_summary: 'סיכום הישיבה בחדר',
	render_error: 'שגיאת תצוגה',
	unhandled_rejection: 'שגיאה לא מטופלת',
	api_error: 'שגיאת שרת',
	facilitator_action_failed: 'פעולת מנחה נכשלה',
	payment_page_error: 'שגיאה בדף התשלום',
	agent_flag_spoofed: 'הודעת "דגל סוכן" ממי שאינו הסוכן (אבטחה)',
	// events only
	quality_changed: 'איכות החיבור השתנתה',
	reconnecting: 'מתחבר/ת מחדש',
	reconnected: 'התחבר/ה מחדש',
	signal_reconnecting: 'ערוץ הבקרה מתחבר מחדש',
	net_online: 'הרשת חזרה',
	net_offline: 'אין רשת',
	net_change: 'סוג הרשת השתנה',
	ice_route: 'מסלול החיבור (ישיר / ממסר)',
	poll_failed: 'רענון העמוד נכשל',
	device_changed: 'התקן השמע הוחלף',
	devices_changed: 'רשימת התקני השמע השתנתה',
	app_background: 'האפליקציה ברקע',
	app_foreground: 'האפליקציה חזרה לחזית',
	app_inactive: 'האפליקציה לא פעילה (שיחה / מרכז הבקרה)',
	app_active: 'האפליקציה פעילה שוב',
	page_hidden: 'הטאב הוסתר',
	page_visible: 'הטאב חזר',
	participant_seen: 'ראה/תה את המנחה בחדר',
	track_subscription_failed: 'קבלת השמע/וידאו של משתתף נכשלה',
	cpu_constrained: 'המכשיר עמוס (CPU)',
	sdk_log: 'אזהרת LiveKit',
	agent_flag_received: 'התקבל דגל סוכן (אצל המנחה)',
};

/** server / system event types (support_circle_events.type) */
export const EVENT_TYPE_LABELS: Record<string, string> = {
	// LiveKit webhook (controller)
	lk_webhook_not_configured: 'webhook של LiveKit לא מוגדר',
	lk_webhook_rejected: 'webhook של LiveKit נדחה (חתימה)',
	lk_webhook_error: 'תקלה בעיבוד webhook של LiveKit',
	lk_webhook_unmatched: 'webhook של LiveKit בלי מעגל / משתתף תואם',
	room_started: 'החדר נפתח',
	room_finished: 'החדר נסגר',
	room_finished_never_started: 'החדר נסגר והמעגל לא התחיל',
	// GH-841 fix 3 (replaces auto_end_premature — a mid-slot room_finished no longer ends the circle)
	room_emptied_mid_slot: 'החדר התרוקן ונסגר באמצע הזמן (המעגל נשאר פעיל)',
	ended_after_room_emptied: 'המעגל נסגר בסוף הזמן — אף אחד לא חזר לחדר',
	observer_presence: 'הסוכן המשקיף בחדר',
	room_anomaly: 'אנומליה בחדר (הקלטה / שידור)',
	observer_dispatch: 'הזנקת הסוכן המשקיף',
	// agent / transcript
	agent_key_rejected: 'מפתח סוכן שגוי',
	agent_transcript_progress: 'התקדמות תמלול',
	agent_flags: 'דגלי סוכן (ספירה)',
	transcript_closed: 'התמלול נסגר',
	summary: 'סיכום AI',
	summary_timer_lost: 'תזמון הסיכום אבד בהפעלה מחדש',
	// payments
	payme_callback: 'callback מ-PayMe',
	settle_sweep: 'סריקת תשלומים',
	client_success_no_callback: 'הלקוח דיווח הצלחה ו-PayMe לא אישר',
	// notifications
	notif_tick: 'סבב התראות',
	notif_switch_changed: 'מתג ההתראות שונה',
	notif_suppressed_by_switch: 'התראות שלא נשלחו כי המתג כבוי',
	claimed_no_outcome: 'הודעה נתבעה ולא נשלחה (קריסה)',
	m1_never_attempted: 'הודעת ההרשמה לא נוסתה',
	notification: 'הודעה',
	notification_delivery: 'סטטוס מסירה',
	delivery: 'סטטוס מסירה',
	tw_status: 'סטטוס מסירה (Twilio)',
	twilio_status: 'סטטוס מסירה (Twilio)',
	tw_callback_rejected: 'callback של Twilio נדחה (חתימה)',
	tw_callback_unmatched: 'callback של Twilio בלי הודעה תואמת',
	sg_event: 'סטטוס מסירה (SendGrid)',
	sg_webhook_rejected: 'webhook של SendGrid נדחה (חתימה)',
	// cron / boot / derived
	cron_tick: 'דופק הקרון',
	server_boot: 'השרת עלה',
	stale_circle: 'מעגל שנתקע פתוח / חי אחרי הזמן',
	webhook_silence: 'אין webhook מ-LiveKit',
	presence_reconciled: 'יישוב נוכחות מול LiveKit',
	circle_summary: 'סיכום המעגל',
	// auth / limits / misc
	auth_refused: 'סירוב התחברות (auth)',
	rate_limited: 'חסימת קצב בקשות (429)',
	foreign_circle: 'טלמטריה למעגל זר',
	confirm_page: 'עמוד "אני מגיע/ה"',
	confirm_page_opened: 'עמוד "אני מגיע/ה"',
};

/** label of a support_circle_events `type` ('lk:participant_joined', 'client:quality_changed', 'cron_tick', …) */
export const eventTypeLabel = (type?: string) => {
	if (!type) return '';
	if (type.startsWith('lk:')) return LK_EVENT_LABELS[type.slice(3)] || `LiveKit: ${type.slice(3)}`;
	if (type.startsWith('client:')) return CLIENT_EVENT_LABELS[type.slice(7)] || type.slice(7);
	return EVENT_TYPE_LABELS[type] || ACTION_LABELS[type] || ACTION_LABELS[`SUPPORT_CIRCLE_${type.toUpperCase()}`] || type;
};
export const clientEventLabel = (e?: string) => (e ? CLIENT_EVENT_LABELS[e] || e : '');

// ------------------------------------------------------------------ reason codes (plan 1.7 + chapter 2 + 3.4)

const STATUS_HE: Record<string, string> = { draft: 'טיוטה', open: 'פתוח', live: 'מתקיים', ended: 'הסתיים', cancelled: 'בוטל' };

export const REASON_LABELS: Record<string, string> = {
	// unified codes (1.7)
	circle_not_found: 'המעגל לא נמצא',
	circle_status_draft: 'המעגל בטיוטה',
	circle_status_open: 'המעגל עדיין לא התחיל',
	circle_status_live: 'המעגל כבר מתקיים',
	circle_status_ended: 'המעגל הסתיים',
	circle_status_cancelled: 'המעגל בוטל',
	slot_over: 'זמן המעגל עבר',
	member_not_found: 'המנוי/ה לא נמצא/ה',
	seat_lock_busy: 'נעילת מקום תפוסה (בקשה מקבילה)',
	race_lost: 'בקשה מקבילה הקדימה',
	livekit_not_configured: 'LiveKit לא מוגדר בשרת',
	bad_identity: 'זהות משתתף לא תקינה',
	bad_token_format: 'קישור אישור לא תקין',
	no_matching_registration: 'אין הרשמה תואמת',
	// client-only codes
	client_network: 'הבקשה לא הגיעה לשרת (רשת)',
	client_timeout: 'הבקשה לשרת לא נענתה בזמן',
	client_bad_response: 'תשובה לא צפויה מהשרת (בלי טוקן)',
	client_module_missing: 'גרסת אפליקציה בלי מודול הווידאו',
	client_unsupported_browser: 'דפדפן לא נתמך',
	// browsing (2.1)
	not_visible: 'המעגל לא מוצג לחברה / לעולם של המנוי/ה',
	audience_mismatch: 'לא מתאים/ה לקהל היעד',
	bad_length: 'אורך טקסט לא תקין',
	rate_limited: 'יותר מדי בקשות',
	// registration (2.2)
	bad_display_name: 'שם בדוי לא תקין',
	reason_too_long: '"מה הביא אותך" ארוך מדי',
	circle_vanished: 'המעגל נמחק תוך כדי',
	full: 'המעגל מלא',
	name_taken: 'השם הבדוי תפוס',
	// payment (2.3)
	payment_start_failed: 'פתיחת התשלום נכשלה',
	link_expired: 'קישור התשלום פג',
	already_paid: 'כבר שולם',
	matched: 'נמצא תשלום תואם',
	upserted_unmatched: 'אין תשלום תואם — נוצר מסמך חדש',
	status_regression: 'סטטוס תשלום חזר אחורה (מ"שולם")',
	duplicate_callback: 'callback כפול',
	failed: 'נכשל',
	declined: 'הכרטיס נדחה',
	cancelled: 'בוטל',
	chargeback: 'הכחשת עסקה (chargeback)',
	confirmed: 'אושר',
	registration_deleted: 'ההרשמה נמחקה',
	duplicate: 'תשלום כפול',
	registration_cancelled: 'ההרשמה בוטלה',
	circle_closed: 'המעגל נסגר',
	circle_full: 'המעגל התמלא',
	circle_cancelled_during_confirm: 'המעגל בוטל בזמן אישור התשלום',
	suspicious_no_server_price: 'תשלום בלי מחיר שרת (חשוד)',
	changed_meanwhile: 'ההרשמה השתנתה תוך כדי',
	completion_handler_exception: 'תקלה בעיבוד תשלום שהושלם',
	hold_expired_while_paying: 'המקום פג בזמן שהחלון היה פתוח',
	refunded: 'הוחזר',
	refund_failed: 'ההחזר נכשל — להחזיר ידנית ב-PayMe',
	already_claimed: 'ההחזר כבר בטיפול',
	payment_not_found: 'התשלום לא נמצא',
	paid_without_payment_id: 'שולם בלי מזהה תשלום',
	settle_stuck: 'תשלום תקוע בסריקה',
	// cancel (2.4)
	not_registered: 'לא רשום/ה',
	too_late: 'מאוחר מדי לבטל',
	participant_not_found: 'המשתתף/ת לא נמצא/ה במעגל', // GH-856
	already_cancelled: 'ההרשמה כבר בוטלה', // GH-856
	// confirm page (2.5)
	unknown_token: 'קישור אישור לא מוכר',
	error: 'תקלה בשרת',
	confirmed_now: 'אושר עכשיו',
	already_confirmed: 'כבר אושר',
	// auth (2.6.1)
	no_token: 'לא מחובר/ת (אין טוקן)',
	jwt_expired: 'טוקן ההתחברות פג',
	jwt_invalid: 'טוקן התחברות לא תקין',
	role_guest: 'משתמש אורח',
	inactive: 'החשבון לא פעיל (נמחק)',
	unverified_666: 'החשבון לא אומת (אין מייל / טלפון מאומת)',
	inviter_missing: 'המזמין/ה נמחק/ה',
	// token (2.6.2)
	too_early: 'מוקדם מדי',
	removed_by_facilitator: 'הוצא/ה מהמעגל על ידי המנחה', // GH-871
	not_removed: 'לא ברשימת המוצאים', // GH-871
	not_mic_locked: 'לא מושתק/ת על ידי המנחה', // GH-878
	mic_lock_error: 'נעילת המיקרופון נכשלה', // GH-878
	mic_unlock_error: 'שחרור המיקרופון נכשל', // GH-878
	participant_required: 'חסר מזהה משתתף', // GH-871
	bad_participant: 'מזהה משתתף לא תקין', // GH-871
	late_2: 'המנחה לא בחדר 2 דק׳ אחרי ההתחלה — נשלח מייל דחוף', // GH-862
	chat_disabled: 'הצ׳אט כבוי על ידי המנחה', // GH-871
	bad_message: 'הודעת צ׳אט ריקה או ארוכה מדי', // GH-871
	chat_rate_limited: 'יותר מדי הודעות צ׳אט בדקה', // GH-871
	not_live: 'המעגל לא פעיל כרגע', // GH-871
	chat_send_error: 'שליחת הודעת הצ׳אט נכשלה', // GH-871
	token_create_error: 'תקלה ביצירת הטוקן',
	no_connect: 'קיבל/ה טוקן ולא התחבר/ה',
	never_registered: 'מעולם לא נרשם/ה',
	payment_pending_valid: 'ממתין/ה לתשלום (המקום עוד שמור)',
	payment_pending_expired: 'ממתין/ה לתשלום (המקום פג)',
	staff_not_host: 'צוות במעגל עם מנחה משובץ',
	wrong_expert: 'מומחה אחר מהמשובץ',
	likely_old_build: 'כנראה גרסת אפליקציה ישנה (כפתור 15 דק׳)',
	// webhook (2.7)
	empty_auth: 'בלי חתימה',
	body_hash_mismatch: 'החתימה לא תואמת לתוכן',
	bad_json: 'JSON לא תקין',
	other: 'אחר',
	connection_aborted: 'החיבור נקטע לפני הכניסה',
	camera_in_audio_only: 'מצלמה במעגל שמע בלבד',
	room_finished: 'החדר נסגר',
	removed: 'הוסר/ה על ידי המנחה',
	// facilitator (2.8)
	not_facilitator: 'אינו/ה המנחה',
	already_present: 'כבר בחדר',
	dispatch_failed: 'ההזנקה נכשלה',
	identity_required: 'חסרה זהות משתתף',
	livekit_error: 'שגיאת LiveKit (לא בחדר?)',
	not_in_room: 'לא בחדר',
	mute_error: 'ההשתקה נכשלה',
	late_5: 'המנחה לא נכנס/ה 5 דק׳ אחרי ההתחלה',
	late_10: 'המנחה לא נכנס/ה 10 דק׳ אחרי ההתחלה',
	never: 'המנחה לא נכנס/ה בכלל',
	in_room_not_started: 'המנחה בחדר ולא לחץ/ה "התחלה" — המשתתפים בלובי',
	ai_no_starter: 'מעגל AI ואף אחד לא מתחיל אותו',
	// agent / transcript (2.9)
	no_transcript: 'אין תמלול',
	has_summary: 'כבר יש סיכום',
	too_few_lines: 'מעט מדי שורות לסיכום',
	crisis_flag: 'דגל משבר מהסוכן',
	// feedback (2.10)
	bad_rating: 'דירוג לא תקין',
	bad_comfort: 'דירוג נוחות לא תקין',
	save_error: 'השמירה נכשלה',
	// notifications (2.11)
	switch_off: 'מתג ההתראות כבוי',
	no_active_participant: 'אין הרשמה פעילה',
	payment_pending: 'ממתין/ה לתשלום',
	member_inactive: 'החשבון נמחק — לא נשלח',
	no_email: 'אין מייל',
	already_notified: 'כבר נשלח',
	no_confirm_token: 'אין קישור אישור',
	no_phone: 'אין טלפון',
	unsafe_url_suffix: 'קישור לא בטוח לכפתור WhatsApp',
	no_sid: 'אין תבנית WhatsApp מאושרת',
	team_facilitator_no_wa: 'מנחה צוות — בלי WhatsApp',
	no_facilitator: 'אין מנחה משובץ',
	facilitator_no_email: 'למנחה אין מייל',
	registered_within_48h: 'נרשם/ה פחות מ-48 שעות לפני — בלי אישור הגעה',
	in_past: 'המועד עבר',
	facilitator_swapped: 'המנחה הוחלף/ה',
	circle_changed_meanwhile: 'המעגל השתנה תוך כדי',
	claimed_no_outcome: 'נתבע ולא נשלח (קריסה)',
	m1_never_attempted: 'הודעת ההרשמה לא נוסתה',
	stale_circle: 'מעגל שלא נסגר',
	provider_rejected: 'הספק דחה את ההודעה',
	send_error: 'השליחה נכשלה',
	// delivery (2.11 + Twilio / SendGrid)
	undelivered: 'לא נמסר',
	bounce: 'חזר (bounce)',
	blocked: 'נחסם',
	dropped: 'הושמט (SendGrid)',
	spamreport: 'סומן כספאם',
	tw_callback_rejected: 'חתימת Twilio לא תקינה',
	sg_webhook_rejected: 'חתימת SendGrid לא תקינה',
	// admin (2.12)
	title_required: 'חסרה כותרת',
	scheduled_at_required: 'חסר מועד',
	bad_price: 'מחיר לא תקין',
	bad_free_for_company: 'חברה לא תקינה ב"חינם עבור"',
	bad_free_for_email: 'מייל לא תקין ב"חינם עבור"',
	bad_status: 'סטטוס לא תקין',
	has_registrations: 'יש נרשמים',
	has_payments: 'יש תשלומים',
	// cron / system (2.13)
	webhook_silence: 'אין webhook מ-LiveKit',
	room_emptied_mid_slot: 'החדר התרוקן באמצע הזמן (המעגל נשאר פעיל)',
	ended_after_room_emptied: 'נסגר בסוף הזמן — אף אחד לא חזר לחדר',
	room_finished_never_started: 'החדר נסגר והמעגל לא התחיל',
	// client telemetry (3.1)
	foreign_circle: 'מעגל שהמנוי/ה לא שייך/ת אליו',
	deep_link_lost: 'הקישור למעגל אבד בהתחברות',
	// generic
	invalid_reason_code: 'קוד סיבה לא תקין (באג בלוג)',
	// free_reason values (also usable as reasons)
	no_price: 'מעגל ללא מחיר',
	host: 'מנחה / צוות (לא משלם/ת)',
	free_for_company: 'חינם לחברה',
	free_for_email: 'חינם לפי מייל החשבון',
	free_for_secondary_email: 'חינם לפי המייל הפרטי המאומת',
	// payment_start_failed error_source values
	invalid_payment: 'בקשת תשלום לא תקינה',
	payme_no_response: 'PayMe לא ענה',
	network: 'תקלת רשת',
	no_payment: 'לא נוצר תשלום',
	late_payment_after_expiry: 'שולם אחרי שהמקום השמור פג',
	client_success_no_callback: 'דף התשלום דיווח הצלחה ואין callback מ-PayMe',
	// payment_<sale_status>
	payment_failed: 'התשלום נכשל',
	payment_declined: 'הכרטיס נדחה',
	payment_cancelled: 'התשלום בוטל',
	payment_chargeback: 'הכחשת עסקה (chargeback)',
	// refund why
	member_cancelled: 'המנוי/ה ביטל/ה',
	admin_cancelled: 'אדמין ביטל/ה את ההרשמה', // GH-856
	circle_cancelled: 'המעגל בוטל',
	hold_released: 'שמירת המקום שוחררה',
	// confirm page scanners
	link_preview: 'תצוגה מקדימה של קישור (WhatsApp וכו׳)',
	mail_scanner: 'סורק מיילים',
	fast_open: 'נפתח מהר מדי אחרי השליחה',
	// LiveKit webhook
	lk_webhook_not_configured: 'webhook של LiveKit — השרת לא מוגדר',
	lk_webhook_rejected: 'webhook של LiveKit נדחה (חתימה)',
	lk_webhook_error: 'טיפול ב-webhook נכשל (LiveKit ינסה שוב)',
	lk_webhook_unmatched: 'webhook בלי מעגל / משתתף תואם',
	no_circle: 'אין מעגל לחדר',
	participant_mismatch: 'המשתתף/ת לא שייך/ת למעגל הזה',
	stale_order: 'אירוע ישן שהגיע באיחור (לא שינה נוכחות)',
	room_anomaly: 'הקלטה / שידור נכנס בחדר (לא צפוי)',
	self_rejoin: 'התחבר/ה מחדש מאותו מכשיר',
	other_device: 'מכשיר אחר עם אותה זהות',
	agent_key_rejected: 'מפתח סוכן שגוי',
	// notification send results
	send_failed: 'השליחה נכשלה',
	provider_error: 'הספק דחה את ההודעה',
	delivered: 'נמסר',
	read: 'נקרא',
	// GH-841 B3 (phase 2 / 3 / 5) + QA 6.2 — copied from the backend registry (supportCircleReasons.ts)
	no_room: 'webhook בלי שם חדר',
	client_http_error: 'שגיאת HTTP בלי קוד מהשרת (למשל 401 מההתחברות)',
	payment_confirm_timeout: 'אישור התשלום לא הגיע בזמן (בקליינט)',
	token_failed: 'בקשת הטוקן נכשלה (בקליינט)',
	connect_failed: 'החיבור לחדר נכשל',
	mic_failed: 'המיקרופון לא עבד',
	camera_failed: 'המצלמה לא עבדה',
	audio_playback_blocked: 'הדפדפן חסם השמעת קול (לא שומע/ת)',
	video_playback_blocked: 'הדפדפן חסם הצגת וידאו',
	audio_session_failed: 'הפעלת השמע באפליקציה נכשלה',
	local_silence_detected: 'המיקרופון שקט (מדבר/ת ואף אחד לא שומע)',
	mic_toggle_failed: 'הפעלה / כיבוי של המיקרופון נכשלו',
	inbound_audio_stalled: 'לא מגיע קול מהחדר',
	bg_reset: 'האפליקציה אופסה אחרי זמן ברקע ויצאה מהחדר',
	network_exit: 'יצא/ה מהחדר בגלל הרשת',
	render_error: 'שגיאת תצוגה במסך',
	unhandled_rejection: 'שגיאה לא מטופלת בקליינט',
	api_error: 'קריאה לשרת נכשלה',
	facilitator_action_failed: 'פעולת מנחה נכשלה',
	payment_page_error: 'שגיאה בדף התשלום',
	agent_flag_spoofed: 'הודעת "דגל סוכן" ממי שאינו הסוכן (זיוף)',
	poll_failed: 'רענון העמוד נכשל',
	track_subscription_failed: 'קבלת שמע / וידאו ממשתתף נכשלה',
	client_foreign_circle: 'טלמטריה למעגל שהחבר/ה לא שייך/ת אליו',
	client_unknown_event: 'אירוע קליינט לא מוכר (נזרק)',
	client_batch_too_large: 'מנת טלמטריה גדולה מדי (נזרקה)',
	client_batch_over_cap: 'יותר מ-25 אירועים במנה (העודף נזרק)',
	client_bad_batch: 'מנת טלמטריה לא תקינה (נזרקה)',
	client_rate_limited: 'יותר מדי מנות טלמטריה בדקה',
	client_ual_over_budget: 'יותר מדי אירועי קליינט בשעה (העודף נשמר רק ביומן הגולמי)',
	no_show: 'נרשם/ה ולא נכנס/ה',
	never_tried: 'לא ניסה/תה להיכנס',
	auth_refused: 'נחסם/ה בהתחברות',
	token_refused: 'ביקש/ה טוקן ונדחה/תה',
	got_token_no_connect: 'קיבל/ה טוקן ולא התחבר/ה',
	on_page_no_token: 'היה/תה בעמוד בזמן ולא ביקש/ה להיכנס',
	summary_timer_lost: 'תזמון הסיכום אבד בהפעלה מחדש',
	restart_mid_circle: 'השרת הופעל מחדש באמצע מעגל',
	notif_suppressed_by_switch: 'התראות שלא נשלחו כי המתג כבוי',
	cron_job_failed: 'משימת קרון נכשלה',
	cron_overlap: 'טיק קרון התחיל לפני שהקודם הסתיים',
	tw_signature_missing: 'callback של Twilio בלי חתימה',
	tw_signature_invalid: 'חתימת callback של Twilio לא תקינה',
	tw_not_configured: 'אימות Twilio לא מוגדר בשרת',
	tw_unmatched: 'callback בלי הודעה תואמת',
	tw_bad_callback: 'callback של Twilio בלי מזהה / סטטוס תקין',
	out_of_order: 'סטטוס שהגיע אחרי סטטוס מתקדם יותר',
};

/** values that ride in metadata (not reasons) — change / why / kind / … */
export const VALUE_LABELS: Record<string, Record<string, string>> = {
	change: {
		cancelled: 'המעגל בוטל',
		rescheduled: 'המועד שונה',
		facilitator_changed: 'המנחה הוחלף/ה',
		unpublished: 'המעגל הוחזר לטיוטה',
		deleted: 'המעגל נמחק',
		ended_by_admin: 'אדמין סיים/ה את המעגל',
		support_fix: 'תיקון support (סקריפט)',
		member_deleted: 'החשבון נמחק',
	},
	why: {
		member_cancelled: 'המנוי/ה ביטל/ה',
		admin_cancelled: 'אדמין ביטל/ה את ההרשמה', // GH-856
		circle_cancelled: 'המעגל בוטל',
		duplicate: 'תשלום כפול',
		registration_cancelled: 'ההרשמה בוטלה',
		circle_closed: 'שילם/ה אחרי שהמעגל נסגר',
		circle_full: 'שילם/ה אחרי שהמקום פג והמעגל התמלא',
		no_matching_registration: 'אין הרשמה תואמת',
		registration_deleted: 'ההרשמה נמחקה',
		settle_stuck: 'תשלום תקוע',
	},
	kind: { new: 'חדשה', reactivated: 'הרשמה מחדש אחרי ביטול', duplicate_submit: 'לחיצה כפולה' },
	free_reason: {
		no_price: 'מעגל חינמי',
		host: 'צוות / מנחה',
		free_for_company: 'חינם לחברה',
		free_for_email: 'חינם למייל',
		free_for_secondary_email: 'חינם למייל הפרטי',
	},
	error_source: { invalid_payment: 'תשלום לא תקין', payme_no_response: 'PayMe לא ענה', network: 'רשת', no_payment: 'לא נוצר תשלום' },
	dup_kind: { self_rejoin: 'אותו מכשיר התחבר מחדש', other_device: 'מכשיר אחר עם אותה זהות', unknown: 'לא ידוע' },
	exit_path: {
		leave_button: 'כפתור יציאה',
		back: 'חזרה',
		navigation: 'ניווט',
		kicked: 'הוסר/ה',
		room_ended: 'המעגל הסתיים',
		duplicate: 'מכשיר אחר',
		network: 'רשת',
		bg_reset: 'איפוס אחרי רקע ארוך',
		unmount: 'המסך נסגר',
	},
	ended_via: { end_button: 'כפתור סיום', admin: 'אדמין', room_finished: 'החדר נסגר (LiveKit)' },
	likely_scanner: { link_preview: 'תצוגה מקדימה (WhatsApp וכו׳)', mail_scanner: 'סורק מיילים', fast_open: 'נפתח מהר מדי אחרי השליחה' },
	state: { form: 'טופס', already_confirmed: 'כבר אושר' },
	role: { participant: 'משתתף/ת', facilitator: 'מנחה', staff_host: 'צוות מנחה', staff: 'צוות', agent: 'סוכן', bad: 'זהות לא תקינה', admin: 'אדמין', system: 'מערכת' },
	actor_kind: { expert: 'מומחה/ית', staff: 'צוות' },
	via: {
		api: 'בקשה',
		webhook: 'webhook',
		cron: 'קרון',
		payme_callback: 'callback של PayMe',
		settle_sweep: 'סריקת תשלומים',
		confirm_page: 'עמוד אישור',
		client: 'אתר / אפליקציה',
		agent: 'סוכן',
		script: 'סקריפט',
		boot: 'עליית שרת',
	},
	route_kind: {
		list: 'רשימה',
		get: 'עמוד מעגל',
		register: 'הרשמה',
		token: 'כניסה',
		feedback: 'משוב',
		start: 'התחלה',
		end: 'סיום',
		remove: 'הסרה',
		mute: 'השתקה',
		suggest: 'הצעת נושא',
		client_events: 'טלמטריה',
		admin: 'אדמין',
	},
	what: { participants: 'נרשמים', transcript: 'תמלול', transcript_export: 'ייצוא תמלול', suggestions: 'הצעות נושאים', timeline: 'יומן' },
	audience_rule: { gender: 'מגדר', parenting: 'הורות', languages: 'שפה', marital_status: 'מצב משפחתי', caregiving: 'מטפל/ת בקרוב' },
	failure: { PermissionDenied: 'אין הרשאה', NotFound: 'לא נמצא התקן', DeviceInUse: 'ההתקן בשימוש', Other: 'אחר' },
	recipient_role: { member: 'מנוי/ה', facilitator: 'מנחה' },
	email_target: { primary: 'מייל ראשי', secondary: 'מייל פרטי' },
	ctx_from: { request: 'מהבקשה', last_token: 'מהטוקן האחרון', none: 'לא ידוע' },
	version_source: { header: 'מהאפליקציה', ua: 'מה-User-Agent', none: 'לא ידוע' },
	clock_from: { token: 'מהטוקן', started_at: 'מההתחלה' },
};
export const valueLabel = (field: string, v: unknown) => {
	if (v === undefined || v === null || v === '') return '';
	const s = String(v);
	return VALUE_LABELS[field]?.[s] || s;
};

const CANCEL_HE: Record<string, string> = {
	member: 'ביטל/ה',
	admin: 'בוטל/ה ע״י אדמין', // GH-856
	payment_expired: 'לא השלים/ה תשלום',
	circle_cancelled: 'המעגל בוטל',
	circle_cancelled_unpaid: 'המעגל בוטל לפני התשלום',
	circle_full: 'המעגל התמלא',
	circle_closed: 'המעגל נסגר',
};

/** Hebrew label of a reason / sub_reason code; parametric codes (circle_status_<x>, role_<r>, validation:<path>,
 *  cancelled:<cancel_reason>, role_not_staff:<role>) are composed; an unknown code comes back as is. */
export const reasonLabel = (code?: string | null): string => {
	if (!code) return '';
	const c = String(code);
	if (REASON_LABELS[c]) return REASON_LABELS[c];
	if (c.startsWith('circle_status_')) return `המעגל במצב ${STATUS_HE[c.slice(14)] || c.slice(14)}`;
	if (c.startsWith('validation:')) return `שגיאת ולידציה בשדה ${c.slice(11)}`;
	if (c.startsWith('cancelled:')) return `נרשם/ה וההרשמה בוטלה (${CANCEL_HE[c.slice(10)] || c.slice(10)})`;
	if (c.startsWith('role_not_staff')) return `התפקיד ${c.slice(14).replace(/^[:_]/, '') || 'הזה'} אינו צוות (לא יכול/ה להנחות)`;
	if (c.startsWith('payment_')) return `סטטוס תשלום: ${c.slice(8)}`;
	if (c.startsWith('role_')) return `תפקיד ${c.slice(5)} לא מורשה`;
	if (c.startsWith('skipped:')) return reasonLabel(c.slice(8));
	if (c.startsWith('client_')) return `שגיאת לקוח: ${c.slice(7)}`;
	return c;
};

// ------------------------------------------------------------------ LiveKit DisconnectReason

export const DISCONNECT_REASON_LABELS: Record<string, string> = {
	UNKNOWN_REASON: 'סיבה לא ידועה',
	CLIENT_INITIATED: 'יצא/ה בעצמו/ה',
	DUPLICATE_IDENTITY: 'התחבר/ה שוב מאותה זהות',
	SERVER_SHUTDOWN: 'שרת LiveKit נסגר',
	PARTICIPANT_REMOVED: 'הוסר/ה על ידי המנחה',
	ROOM_DELETED: 'החדר נסגר (סיום)',
	STATE_MISMATCH: 'חוסר תאימות מצב',
	JOIN_FAILURE: 'הכניסה נכשלה',
	MIGRATION: 'מעבר שרת',
	SIGNAL_CLOSE: 'ערוץ הבקרה נסגר',
	ROOM_CLOSED: 'החדר נסגר',
	USER_UNAVAILABLE: 'המשתמש לא זמין',
	USER_REJECTED: 'המשתמש דחה',
	SIP_TRUNK_FAILURE: 'תקלת SIP',
	CONNECTION_TIMEOUT: 'החיבור פג (רשת)',
	MEDIA_FAILURE: 'תקלת מדיה (שמע / וידאו)',
	room_finished: 'החדר נסגר',
};
/** the disconnect reasons that are a normal exit (everything else = a failure, plan 2.7) */
export const NORMAL_DISCONNECTS = ['CLIENT_INITIATED', 'ROOM_DELETED', 'ROOM_CLOSED', 'MIGRATION', 'PARTICIPANT_REMOVED', 'room_finished'];
export const disconnectLabel = (r?: string | number | null) => {
	if (r === undefined || r === null || r === '') return '';
	return DISCONNECT_REASON_LABELS[String(r)] || String(r);
};

// ------------------------------------------------------------------ Twilio error codes (WhatsApp delivery)

export const TWILIO_ERROR_LABELS: Record<string, string> = {
	'63049': 'מטא חסמה הודעת Marketing (התבנית סווגה כשיווקית)',
	'63016': 'מחוץ לחלון 24 השעות — נדרשת תבנית מאושרת',
	'63024': 'נמען לא תקין',
	'63003': 'המכשיר לא זמין / אין WhatsApp במספר',
	'63018': 'חריגה מקצב השליחה',
	'21211': 'מספר טלפון לא תקין',
	'21610': 'החבר/ה שלח/ה STOP (הוסר/ה מהתפוצה)',
	'21614': 'המספר אינו נייד',
	'21408': 'שליחה לאזור הזה לא מופעלת בחשבון',
	'21656': 'משתני התבנית לא תקינים',
	'63001': 'הערוץ לא הצליח לאמת את הבקשה',
	'63005': 'הערוץ לא קיבל את התוכן',
	'63007': 'מספר השולח לא מוגדר ל-WhatsApp',
	'63013': 'הערוץ הושבת בגלל הפרת מדיניות',
	'63021': 'תוכן לא תקין לערוץ',
	'63027': 'התבנית לא קיימת בשפה הזו',
	'63028': 'מספר המשתנים לא תואם לתבנית',
	'63030': 'פרמטר לא נתמך בערוץ',
	'63032': 'מגבלה של WhatsApp על המשתמש הזה',
	'63051': 'חשבון WhatsApp Business נעול',
	'30001': 'תור השליחה מלא',
	'30002': 'החשבון מושעה',
	'30003': 'המכשיר לא זמין',
	'30004': 'ההודעה נחסמה',
	'30005': 'מספר לא ידוע / לא קיים',
	'30006': 'קו נייח או ספק לא זמין',
	'30007': 'הספק סינן את ההודעה',
	'30008': 'שגיאה לא ידועה',
	'11200': 'ה-callback לא נענה (HTTP)',
};
export const twilioErrorLabel = (code?: string | number | null) => {
	if (code === undefined || code === null || code === '') return '';
	const c = String(code);
	return TWILIO_ERROR_LABELS[c] ? `${c} · ${TWILIO_ERROR_LABELS[c]}` : c;
};

// ------------------------------------------------------------------ delivery statuses (Twilio + SendGrid)

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
	accepted: 'התקבל',
	scheduled: 'מתוזמן',
	queued: 'בתור',
	sending: 'בשליחה',
	sent: 'נשלח',
	delivered: 'נמסר',
	read: 'נקרא',
	undelivered: 'לא נמסר',
	failed: 'נכשל',
	canceled: 'בוטל',
	processed: 'עובד (SendGrid)',
	deferred: 'נדחה זמנית',
	bounce: 'חזר (bounce)',
	blocked: 'נחסם',
	dropped: 'הושמט',
	spamreport: 'סומן כספאם',
};
const DELIVERY_RANK: Record<string, number> = {
	accepted: 1,
	scheduled: 1,
	queued: 1,
	processed: 1,
	sending: 2,
	deferred: 2,
	sent: 3,
	delivered: 5,
	read: 6,
	undelivered: 4,
	failed: 4,
	canceled: 4,
	bounce: 4,
	blocked: 4,
	dropped: 4,
	spamreport: 4,
};
export const deliveryRank = (s?: string) => (s ? DELIVERY_RANK[s] || 0 : 0);
export const isDeliveryFailure = (s?: string) =>
	!!s && ['undelivered', 'failed', 'canceled', 'bounce', 'blocked', 'dropped', 'spamreport'].includes(s);
export const deliveryLabel = (s?: string) => (s ? DELIVERY_STATUS_LABELS[s] || s : '');

// ------------------------------------------------------------------ notification steps

export type Channel = 'email' | 'whatsapp';
export const CHANNEL_LABELS: Record<string, string> = { email: 'מייל', whatsapp: 'WhatsApp' };
export interface StepDef {
	step: string;
	code: string;
	label: string;
	marker?: string; // participant.notified.<marker> / circle.<field>
	legacyMarker?: string; // GH-878: the marker of the same step before its timing changed
	channels: Channel[];
}
export const MEMBER_STEPS: StepDef[] = [
	{ step: 'M1_registered', code: 'M1', label: 'הרשמה', marker: 'registered_at', channels: ['email'] },
	{ step: 'M2_confirm_48h', code: 'M2', label: 'אישור 48ש׳', marker: 'confirm_48h_at', channels: ['email', 'whatsapp'] },
	{ step: 'M3_reminder_24h', code: 'M3', label: '24ש׳', marker: 'reminder_24h_at', channels: ['email', 'whatsapp'] },
	// GH-878 (4.11.2026): M4 is email only; the WhatsApps go an hour before (M4b) and 10 minutes before (M5, was 30 minutes —
	// an older circle's M5 rows / its reminder_30m_at marker show in the same M5 column)
	{ step: 'M4_reminder_2h', code: 'M4', label: 'שעתיים', marker: 'reminder_2h_at', channels: ['email'] },
	{ step: 'M4b_whatsapp_1h', code: 'M4b', label: 'שעה', marker: 'reminder_1h_at', channels: ['whatsapp'] },
	{ step: 'M5_reminder_10m', code: 'M5', label: '10 דק׳', marker: 'reminder_10m_at', legacyMarker: 'reminder_30m_at', channels: ['whatsapp'] },
	{ step: 'M6_feedback', code: 'M6', label: 'משוב', marker: 'feedback_at', channels: ['email'] },
	{ step: 'M7_cancelled', code: 'M7', label: 'ביטול', marker: 'cancelled_at', channels: ['email'] },
];
export const FACILITATOR_STEPS: StepDef[] = [
	{ step: 'F1_facilitator_assigned', code: 'F1', label: 'זימון', marker: 'facilitator_notified', channels: ['email'] },
	{ step: 'F2_facilitator_24h', code: 'F2', label: '24ש׳', marker: 'facilitator_24h_at', channels: ['email'] },
	{ step: 'F3_facilitator_2h', code: 'F3', label: 'שעתיים', marker: 'facilitator_2h_at', channels: ['email'] },
];
/** 'M3_reminder_24h' / 'M3' → 'M3' */
export const stepCode = (step?: string) => (step ? String(step).split('_')[0] : '');
// GH-878: steps whose timing changed keep their old label on old rows
const LEGACY_STEP_LABELS: Record<string, string> = { M5_reminder_30m: 'M5 חצי שעה' };
export const stepLabel = (step?: string) => {
	if (step && LEGACY_STEP_LABELS[step]) return LEGACY_STEP_LABELS[step];
	const code = stepCode(step);
	const def = MEMBER_STEPS.concat(FACILITATOR_STEPS).find((s) => s.code === code);
	return def ? `${def.code} ${def.label}` : step || '';
};

// ------------------------------------------------------------------ areas (timeline filter, plan 5.2)

export type Area = 'registration' | 'payment' | 'notifications' | 'auth' | 'join' | 'room' | 'facilitator' | 'admin' | 'system';
export const AREA_LABELS: Record<Area, string> = {
	registration: 'הרשמה',
	payment: 'תשלום',
	notifications: 'התראות',
	auth: 'auth',
	join: 'כניסה',
	room: 'חדר',
	facilitator: 'מנחה',
	admin: 'אדמין',
	system: 'מערכת',
};
export const AREAS = Object.keys(AREA_LABELS) as Area[];

const ACTION_AREA: Record<string, Area> = {
	SUPPORT_CIRCLE_LIST_VIEWED: 'registration',
	SUPPORT_CIRCLE_VIEWED: 'registration',
	SUPPORT_CIRCLE_TOPIC_SUGGESTED: 'registration',
	SUPPORT_CIRCLE_REGISTERED: 'registration',
	SUPPORT_CIRCLE_REGISTRATION_EDITED: 'registration',
	SUPPORT_CIRCLE_UNREGISTERED: 'registration',
	SUPPORT_CIRCLE_CHANGED_FOR_MEMBER: 'registration',
	SUPPORT_CIRCLE_CONFIRM_PAGE_OPENED: 'registration',
	SUPPORT_CIRCLE_ATTENDANCE_CONFIRMED: 'registration',
	SUPPORT_CIRCLE_FEEDBACK_SUBMITTED: 'registration',
	SUPPORT_CIRCLE_SEAT_HELD: 'payment',
	SUPPORT_CIRCLE_PAYMENT_LINK_OPENED: 'payment',
	SUPPORT_CIRCLE_PAYMENT_STATUS: 'payment',
	SUPPORT_CIRCLE_PAYMENT_CONFIRMED: 'payment',
	SUPPORT_CIRCLE_HOLD_EXPIRED: 'payment',
	SUPPORT_CIRCLE_REFUND: 'payment',
	SUPPORT_CIRCLE_NOTIFICATION: 'notifications',
	SUPPORT_CIRCLE_NOTIFICATION_DELIVERY: 'notifications',
	SUPPORT_CIRCLE_AUTH_REFUSED: 'auth',
	SUPPORT_CIRCLE_JOIN_TOKEN: 'join',
	SUPPORT_CIRCLE_TOKEN_NOT_USED: 'join',
	SUPPORT_CIRCLE_ROOM_CONNECTION_ABORTED: 'join',
	SUPPORT_CIRCLE_CLIENT_CONNECT: 'join',
	SUPPORT_CIRCLE_ROOM_JOINED: 'room',
	SUPPORT_CIRCLE_ROOM_LEFT: 'room',
	SUPPORT_CIRCLE_ROOM_TRACK: 'room',
	SUPPORT_CIRCLE_NO_SHOW: 'room',
	SUPPORT_CIRCLE_ATTENDANCE_SUMMARY: 'room',
	SUPPORT_CIRCLE_CLIENT_MEDIA: 'room',
	SUPPORT_CIRCLE_CLIENT_DISCONNECTED: 'room',
	SUPPORT_CIRCLE_CLIENT_SESSION: 'room',
	SUPPORT_CIRCLE_STARTED: 'facilitator',
	SUPPORT_CIRCLE_ENDED: 'facilitator',
	SUPPORT_CIRCLE_PARTICIPANT_REMOVED: 'facilitator',
	SUPPORT_CIRCLE_PARTICIPANT_MUTED: 'facilitator',
	SUPPORT_CIRCLE_PARTICIPANT_UNMUTED: 'facilitator', // GH-878
	SUPPORT_CIRCLE_PARTICIPANT_RESTORED: 'facilitator', // GH-871
	SUPPORT_CIRCLE_PARTICIPANT_RENAMED: 'room', // GH-871
	SUPPORT_CIRCLE_CHAT_MESSAGE: 'room', // GH-871
	SUPPORT_CIRCLE_CHAT_TOGGLED: 'facilitator', // GH-871
	SUPPORT_CIRCLE_FACILITATOR_NO_SHOW: 'facilitator',
};
const FLOW_JOIN_EVENTS = ['enter_button_state', 'enter_clicked', 'room_opened', 'lobby_entered', 'lobby_left', 'ended_before_enter'];
const ERROR_WHERE_AREA: Record<string, Area> = {
	payment: 'payment',
	register: 'registration',
	cancel: 'registration',
	feedback: 'registration',
	list: 'registration',
	home_card: 'registration',
	circle_page: 'registration',
	room_poll: 'room',
};

/** the primary area of a row (action for UAL rows, type for events) */
export const areaOf = (r: { action?: string; type?: string; event?: string; meta?: Record<string, any> }): Area => {
	const m = r.meta || {};
	if (r.action) {
		if (r.action.startsWith('SUPPORT_CIRCLE_ADMIN_')) return 'admin';
		if (r.action === 'SUPPORT_CIRCLE_CLIENT_FLOW') {
			const e = String(r.event || m.event || '');
			if (e.startsWith('payment_') || e === 'hold_expired_seen') return 'payment';
			return FLOW_JOIN_EVENTS.includes(e) ? 'join' : 'registration';
		}
		if (r.action === 'SUPPORT_CIRCLE_CLIENT_ERROR') {
			const e = String(r.event || m.event || '');
			if (e === 'facilitator_action_failed') return 'facilitator';
			if (e === 'payment_page_error') return 'payment';
			if (e === 'agent_flag_spoofed') return 'system';
			return ERROR_WHERE_AREA[String(m.where || '')] || 'room';
		}
		return ACTION_AREA[r.action] || 'system';
	}
	const t = String(r.type || '');
	if (t.startsWith('lk:participant_') || t.startsWith('lk:track_')) return 'room';
	if (t.startsWith('client:')) return t === 'client:poll_failed' ? 'join' : 'room';
	if (t.startsWith('payme') || t === 'settle_sweep' || t === 'client_success_no_callback') return 'payment';
	if (t.startsWith('notif') || t.startsWith('tw') || t.startsWith('sg') || t.includes('delivery') || t === 'claimed_no_outcome' || t === 'm1_never_attempted')
		return 'notifications';
	if (t.startsWith('auth') || t === 'rate_limited') return 'auth';
	if (t === 'observer_dispatch') return 'facilitator';
	if (t.startsWith('SUPPORT_CIRCLE_')) return ACTION_AREA[t] || 'system';
	return 'system';
};

// ------------------------------------------------------------------ admin diff field names (2.12)

export const FIELD_LABELS: Record<string, string> = {
	title: 'כותרת',
	'title.he': 'כותרת (עברית)',
	'title.en': 'כותרת (אנגלית)',
	topic: 'נושא',
	'topic.he': 'נושא (עברית)',
	'topic.en': 'נושא (אנגלית)',
	description: 'תיאור',
	'description.he': 'תיאור (עברית)',
	'description.en': 'תיאור (אנגלית)',
	rules: 'כללים',
	'rules.he': 'כללים (עברית)',
	'rules.en': 'כללים (אנגלית)',
	facilitator: 'מנחה',
	facilitator_type: 'סוג מנחה',
	scheduled_at: 'מועד',
	duration_minutes: 'אורך',
	max_participants: 'מקסימום משתתפים',
	companies: 'חברות',
	excluded_companies: 'חברות מוסתרות',
	is_global: 'גלובלי',
	audio_only: 'שמע בלבד',
	agent_observer: 'סוכן משקיף',
	audience: 'קהל יעד',
	status: 'סטטוס',
	image: 'תמונה',
	price: 'מחיר',
	currency: 'מטבע',
	free_for: 'חינם עבור',
	'free_for.companies': 'חינם עבור — חברות',
	'free_for.emails': 'חינם עבור — מיילים',
	is_test: 'מעגל בדיקה',
	room_name: 'שם חדר',
};
export const fieldLabel = (f: string) => FIELD_LABELS[f] || f;
export const statusLabel = (s?: string) => (s ? STATUS_HE[s] || s : '');

// ------------------------------------------------------------------ one Hebrew line per row

export interface SummaryRow {
	action?: string;
	type?: string;
	event?: string;
	result?: string;
	reason?: string;
	subReason?: string;
	meta: Record<string, any>;
	lk?: Record<string, any>;
}

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
/** "12 דק׳ לפני" / "3 דק׳ אחרי" (minutes_to_start: negative = after the start) */
export const fmtToStart = (m: unknown) => {
	const v = n(m);
	if (v === undefined) return '';
	const r = Math.round(v);
	if (r === 0) return 'בזמן';
	return r > 0 ? `${r} דק׳ לפני` : `${-r} דק׳ אחרי`;
};
export const fmtSeconds = (s: unknown) => {
	const v = n(s);
	if (v === undefined) return '';
	if (v < 90) return `${Math.round(v)} שנ׳`;
	return `${Math.round(v / 60)} דק׳`;
};
const join = (parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' · ');

/** The Hebrew one-liner for a timeline row. `fallback` = the server's summary_he (used only for an unknown row). */
export const summarizeRow = (r: SummaryRow, fallback?: string): string => {
	const m = r.meta || {};
	const reason = r.reason || m.reason;
	const sub = r.subReason || m.sub_reason;
	const failed = !!r.result && r.result !== 'SUCCESS';
	const why = failed && reason ? reasonLabel(reason) : '';
	const subTxt = sub ? reasonLabel(sub) : '';
	const ev = r.event || m.event;
	const a = r.action;

	if (a) {
		switch (a) {
			case 'SUPPORT_CIRCLE_JOIN_TOKEN':
				if (!failed)
					return join([
						m.circle_status === 'open' ? 'טוקן הונפק (ממתין/ה בלובי)' : 'טוקן הונפק',
						valueLabel('role', m.role),
						m.nth_token ? `טוקן #${m.nth_token}` : '',
						fmtToStart(m.minutes_to_start),
						m.was_removed ? 'הוסר/ה קודם ע"י המנחה וחוזר/ת' : '',
						m.other_installation_recent ? 'מכשיר אחר עם אותה זהות' : '',
					]);
				return join([
					`טוקן נדחה — ${why}`,
					subTxt,
					fmtToStart(m.minutes_to_start),
					n(m.client_skew_s) !== undefined && Math.abs(m.client_skew_s) >= 30
						? `שעון המכשיר ${m.client_skew_s > 0 ? 'מקדים' : 'מאחר'} ב-${fmtSeconds(Math.abs(m.client_skew_s))}`
						: '',
					m.ended_via ? `הסתיים דרך ${valueLabel('ended_via', m.ended_via)}` : '',
				]);
			case 'SUPPORT_CIRCLE_AUTH_REFUSED':
				return join([
					`סירוב auth — ${reasonLabel(reason) || 'לא ידוע'}`,
					n(m.expired_minutes_ago) !== undefined ? `פג לפני ${Math.round(m.expired_minutes_ago)} דק׳` : '',
					m.route_kind ? valueLabel('route_kind', m.route_kind) : '',
					n(m.recovered_within_s) !== undefined ? `התאושש/ה תוך ${fmtSeconds(m.recovered_within_s)}` : '',
					m.suppressed_before ? `(+${m.suppressed_before} דומים)` : '',
				]);
			case 'SUPPORT_CIRCLE_TOKEN_NOT_USED':
				return join([
					'קיבל/ה טוקן ולא התחבר/ה',
					n(m.minutes_since_clock_start) !== undefined ? `${Math.round(m.minutes_since_clock_start)} דק׳` : '',
					m.client_events_seen === false ? 'הקליינט לא דיווח כלום' : m.last_client_event ? `אחרון: ${clientEventLabel(m.last_client_event)}` : '',
				]);
			case 'SUPPORT_CIRCLE_ROOM_JOINED':
				return join([
					'נכנס/ה לחדר',
					m.join_index ? `כניסה #${m.join_index}` : '',
					n(m.token_to_join_s) !== undefined ? `${fmtSeconds(m.token_to_join_s)} מהטוקן / ההתחלה` : '',
					fmtToStart(m.minutes_to_start),
					m.joined_while_open ? 'התחבר/ה כשהמעגל עוד לא התחיל' : '',
				]);
			case 'SUPPORT_CIRCLE_ROOM_LEFT':
				return join([
					`יצא/ה — ${disconnectLabel(m.disconnect_reason || reason) || 'לא ידוע'}`,
					n(m.stay_seconds) !== undefined ? `אחרי ${fmtSeconds(m.stay_seconds)}` : '',
					m.dup_kind ? valueLabel('dup_kind', m.dup_kind) : '',
				]);
			case 'SUPPORT_CIRCLE_ROOM_TRACK':
				return join([
					`${m.source === 'CAMERA' ? 'מצלמה' : m.source === 'MICROPHONE' ? 'מיקרופון' : m.source || 'track'} ${m.unpublished || m.event === 'track_unpublished' ? 'הוסר/ה' : 'פורסם/ה'}`,
					m.muted ? 'מושתק' : '',
					why,
				]);
			case 'SUPPORT_CIRCLE_NOTIFICATION':
				return join([
					`${stepLabel(m.step)} ${CHANNEL_LABELS[m.channel] || m.channel || ''}`.trim(),
					r.result === 'SUCCESS' ? 'נשלח' : r.result === 'ERROR' ? `נכשל${m.error_code ? ` (${twilioErrorLabel(m.error_code)})` : ''}` : `דולג — ${reasonLabel(reason)}`,
					m.date_text || m.time_text ? `"${[m.date_text, m.time_text].filter(Boolean).join(' ')}"` : '',
				]);
			case 'SUPPORT_CIRCLE_NOTIFICATION_DELIVERY':
				return join([
					`${stepLabel(m.step)} ${CHANNEL_LABELS[m.channel] || m.channel || ''}`.trim(),
					deliveryLabel(m.status) || reasonLabel(reason),
					m.error_code ? twilioErrorLabel(m.error_code) : '',
				]);
			case 'SUPPORT_CIRCLE_REGISTERED':
				if (!failed)
					return join([
						`נרשם/ה${m.kind && m.kind !== 'new' ? ` (${valueLabel('kind', m.kind)})` : ''}`,
						m.free_reason ? valueLabel('free_reason', m.free_reason) : '',
						fmtToStart(m.minutes_to_start),
						m.registered_while_live ? 'בזמן שהמעגל מתקיים' : '',
					]);
				return join([`הרשמה נדחתה — ${why}`, m.audience_rule ? valueLabel('audience_rule', m.audience_rule) : '']);
			case 'SUPPORT_CIRCLE_SEAT_HELD':
				return join([
					failed ? `שמירת מקום נכשלה — ${why}` : 'מקום נשמר לתשלום',
					m.error_source ? valueLabel('error_source', m.error_source) : '',
					n(m.amount) !== undefined ? `${m.amount} ${m.currency || ''}`.trim() : '',
					m.reused ? 'אותו קישור' : '',
					m.repriced ? 'המחיר השתנה תוך כדי' : '',
				]);
			case 'SUPPORT_CIRCLE_PAYMENT_STATUS':
				return join([`סטטוס תשלום: ${m.from || '?'} → ${m.to || '?'}`, why]);
			case 'SUPPORT_CIRCLE_PAYMENT_CONFIRMED':
				return join([
					failed ? `התשלום לא אושר — ${why}` : `תשלום אושר${reason === 'already_paid' ? ' (כבר שולם)' : ''}`,
					m.via ? valueLabel('via', m.via) : '',
					m.late_payment_after_expiry ? 'שולם אחרי שהמקום פג' : '',
				]);
			case 'SUPPORT_CIRCLE_REFUND':
				return join([
					r.result === 'SUCCESS' ? 'הוחזר' : r.result === 'ERROR' ? 'ההחזר נכשל — להחזיר ידנית ב-PayMe' : `החזר לא בוצע — ${why}`,
					n(m.amount) !== undefined ? `${m.amount} ${m.currency || ''}`.trim() : '',
					m.why ? valueLabel('why', m.why) : '',
					m.member_notified === false ? 'בלי הודעה למנוי/ה' : '',
				]);
			case 'SUPPORT_CIRCLE_HOLD_EXPIRED':
				return join(['שמירת המקום פגה', sub ? reasonLabel(sub) : '', m.link_opened ? 'הקישור נפתח' : 'הקישור לא נפתח']);
			case 'SUPPORT_CIRCLE_UNREGISTERED':
				return failed ? `ביטול נדחה — ${why}` : join(['ביטל/ה הרשמה', fmtToStart(m.minutes_to_start), m.refund_outcome ? `החזר: ${reasonLabel(m.refund_outcome)}` : '']);
			case 'SUPPORT_CIRCLE_CHANGED_FOR_MEMBER':
				return join([valueLabel('change', m.change) || 'שינוי', m.from && m.to ? `${m.from} → ${m.to}` : '']);
			case 'SUPPORT_CIRCLE_CONFIRM_PAGE_OPENED':
				return join([failed ? `עמוד אישור — ${why}` : `עמוד אישור נפתח (${valueLabel('state', m.state) || 'טופס'})`, m.likely_scanner ? `כנראה ${valueLabel('likely_scanner', m.likely_scanner)}` : '']);
			case 'SUPPORT_CIRCLE_ATTENDANCE_CONFIRMED':
				return failed ? `אישור הגעה נכשל — ${why}` : reason === 'already_confirmed' ? 'אישר/ה הגעה (כבר אושר)' : 'אישר/ה הגעה';
			case 'SUPPORT_CIRCLE_STARTED':
				return failed
					? `התחלה נדחתה — ${why}`
					: join([
							'המעגל התחיל',
							m.status_before && m.status_before !== 'open' ? `מ-${statusLabel(m.status_before)}` : '',
							n(m.registered_count) !== undefined ? `${m.registered_count} רשומים` : '',
							n(m.in_room_count) !== undefined ? `${m.in_room_count} בחדר` : '',
							n(m.waiting_in_lobby) !== undefined ? `${m.waiting_in_lobby} בלובי${m.waiting_partial ? ' (web בלבד)' : ''}` : '',
							n(m.minutes_late) !== undefined && m.minutes_late > 0 ? `באיחור ${Math.round(m.minutes_late)} דק׳` : '',
					  ]);
			case 'SUPPORT_CIRCLE_ENDED':
				return failed ? `סיום נדחה — ${why}` : join(['המעגל הסתיים', n(m.duration_min) !== undefined ? `${Math.round(m.duration_min)} דק׳` : '', m.re_end ? 'סיום חוזר' : '']);
			case 'SUPPORT_CIRCLE_FACILITATOR_NO_SHOW':
				return join([reasonLabel(reason) || 'המנחה לא הגיע/ה', n(m.waiting_in_lobby) !== undefined ? `${m.waiting_in_lobby} ממתינים בלובי` : '']);
			case 'SUPPORT_CIRCLE_PARTICIPANT_REMOVED':
			case 'SUPPORT_CIRCLE_PARTICIPANT_MUTED':
			case 'SUPPORT_CIRCLE_PARTICIPANT_UNMUTED': // GH-878
			case 'SUPPORT_CIRCLE_PARTICIPANT_RESTORED': // GH-871
				return join([
					actionLabel(a),
					m.as === 'target' ? '(המשתתף/ת)' : '',
					a === 'SUPPORT_CIRCLE_PARTICIPANT_MUTED' && n(m.tracks_muted) === 0 && !failed ? 'לא היה מה להשתיק' : '',
					a === 'SUPPORT_CIRCLE_PARTICIPANT_MUTED' && m.mic_locked && !failed ? 'המיקרופון ננעל' : '', // GH-878
					why,
				]);
			case 'SUPPORT_CIRCLE_PARTICIPANT_RENAMED': // GH-871 — never the pseudonym itself, only who changed it
				return failed ? `שינוי שם נדחה — ${why}` : join(['שינוי שם בחדר', m.self ? 'בעצמו/ה' : 'על ידי המנחה']);
			case 'SUPPORT_CIRCLE_CHAT_MESSAGE': // GH-871 — refusals only, never the text
				return `הודעת צ׳אט נדחתה — ${why}`;
			case 'SUPPORT_CIRCLE_CHAT_TOGGLED': // GH-871
				return failed ? `שינוי מצב הצ׳אט נדחה — ${why}` : m.enabled ? 'המנחה הפעיל/ה את הצ׳אט' : 'המנחה כיבה/תה את הצ׳אט';
			case 'SUPPORT_CIRCLE_FEEDBACK_SUBMITTED':
				return failed
					? `משוב נדחה — ${why}`
					: join(['שלח/ה משוב', m.rating ? `★ ${m.rating}/5` : '', m.comfort ? `נוחות ${m.comfort}/5` : '', m.has_comment ? 'עם הערה' : '', m.edited ? 'עדכון' : '']);
			case 'SUPPORT_CIRCLE_NO_SHOW':
				return join(['לא הגיע/ה', m.was_on_page_in_window ? 'היה/תה בעמוד בזמן החלון' : '', m.last_failure_reason ? `כישלון אחרון: ${reasonLabel(m.last_failure_reason)}` : '']);
			case 'SUPPORT_CIRCLE_ATTENDANCE_SUMMARY':
				return join([
					m.joined ? 'השתתף/ה' : 'לא השתתף/ה',
					n(m.total_min) !== undefined ? `${Math.round(m.total_min)} דק׳` : '',
					n(m.reconnects) ? `${m.reconnects} חיבורים מחדש` : '',
				]);
			case 'SUPPORT_CIRCLE_ADMIN_UPDATED':
				return failed
					? `עריכה נדחתה — ${why}`
					: `עריכה: ${(Array.isArray(m.changed_fields) ? m.changed_fields : Object.keys(m.changes || {})).map(fieldLabel).join(', ') || 'בלי שינוי'}`;
			case 'SUPPORT_CIRCLE_ADMIN_STATUS_CHANGED':
				return failed ? `שינוי סטטוס נדחה — ${why}` : `סטטוס: ${statusLabel(m.from)} → ${statusLabel(m.to)}`;
			case 'SUPPORT_CIRCLE_ADMIN_VIEWED':
				return `צפייה: ${valueLabel('what', m.what) || m.what || ''}`;
			default:
				break;
		}
		if (a.startsWith('SUPPORT_CIRCLE_CLIENT_')) {
			const label = clientEventLabel(ev) || actionLabel(a);
			return join([
				label,
				failed && reason ? reasonLabel(reason) : '',
				m.reason_name ? disconnectLabel(m.reason_name) : '',
				m.exit_path ? valueLabel('exit_path', m.exit_path) : '',
				m.failure ? valueLabel('failure', m.failure) : '',
				m.http_status ? `HTTP ${m.http_status}` : '',
				m.server_code ? reasonLabel(m.server_code) : '',
			]);
		}
		if (ACTION_LABELS[a]) return join([actionLabel(a), why, subTxt]);
		return fallback || a;
	}

	const t = r.type || '';
	const lk = r.lk || {};
	if (t.startsWith('lk:')) {
		return join([
			eventTypeLabel(t),
			lk.identity_kind ? valueLabel('role', lk.identity_kind) || lk.identity_kind : '',
			lk.disconnect_reason ? disconnectLabel(lk.disconnect_reason) : '',
			lk.track_source ? String(lk.track_source) : '',
			m.stale_order ? 'הגיע לא לפי הסדר' : '',
		]);
	}
	if (t.startsWith('client:')) return join([eventTypeLabel(t), why, m.offline_ms ? `${fmtSeconds(m.offline_ms / 1000)} בלי רשת` : '']);
	const known = !!EVENT_TYPE_LABELS[t] || !!ACTION_LABELS[t];
	if (!known && fallback) return fallback;
	const status = m.status || m.to;
	return join([
		eventTypeLabel(t),
		status && (t.includes('delivery') || t.startsWith('tw') || t.startsWith('sg')) ? deliveryLabel(status) : '',
		m.error_code ? twilioErrorLabel(m.error_code) : '',
		reason ? reasonLabel(reason) : '',
		subTxt,
	]);
};
